/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import useAbortController from '../common/hooks/useAbortController';
import { createContext, ReactNode, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosCall from '../services/axios';
import useForm from '../common/hooks/useForm';
import { setAngelAISessionData } from '../storage/userStorage';

// Define the fields for the login form
type TLoginServicerFields = {
  email?: string;
  isPasswordVisible?: boolean;
  password?: string;
};

const CONTEXT_STORE: TLoginServicerFields = {
  email: '',
  isPasswordVisible: false,
  password: '',
};

const useLoginServicerData = () => {
  const navigate = useNavigate();
  const { reset } = useAbortController();

  const onSubmit = useCallback(
    async (fields: TLoginServicerFields) => {
      const sessionRequest = {
        url: `session`,
        method: 'POST',
        data: {
          ...(fields.email.length === 4 ? { username: fields.email } : { email: fields.email || '' }),
          password: fields.password || '',
        },
      };

      const config = reset();
      const session = await axiosCall(sessionRequest, config);

      const headers = {
        'Session-Token': session.token,
      };

      const userRequest = {
        url: `live/master/user/${session.userId}`,
      };

      const user = await axiosCall(userRequest, config, headers);

      const hashRequest = {
        url: 'session/hash/generate',
        method: 'POST',
        data: {
          expirationTime: 600000, // almost a full week
          encrypt: true,
          parameters: {
            servicerUser: true,
            ...session,
            ...user,
          },
        },
      };

      const hash = await axiosCall(hashRequest, config, headers);

      // Set AngelAI widget Session Storage values immediately after successful servicer login
      setAngelAISessionData(fields.email, 'servicer', session.token);

      navigate(`/select-loans?key=${hash.key}`, { state: { servicer: true } });
      window.location.reload();
    },
    [reset, navigate],
  );

  // Using useForm hook to manage form state and submission
  const { fields, errors, isSubmitting, setFields, handleChange, handleSubmit, handleReset } = useForm(
    CONTEXT_STORE,
    onSubmit,
  );

  return { fields, errors, isSubmitting, setFields, handleChange, handleSubmit, handleReset };
};

type TUseLoginServicerData = ReturnType<typeof useLoginServicerData>;

// Create a context to store login data
const LoginServicerContext = createContext<TUseLoginServicerData | undefined>(undefined);

// Custom hook to use the LoginServicerContext
export const useLoginServicerContext = (): TUseLoginServicerData => {
  const loginServicerContext = useContext(LoginServicerContext);

  if (!loginServicerContext) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('useLoginServicerContext must be used within a LoginServicerProvider');
  }

  return loginServicerContext;
};

type TLoginServicerProviderProps = {
  children: ReactNode;
};

// Provider component for LoginServicerContext
export const LoginServicerProvider = ({ children }: TLoginServicerProviderProps) => {
  return <LoginServicerContext.Provider value={useLoginServicerData()}>{children}</LoginServicerContext.Provider>;
};
